// Kommission für Feste: Ware geht zum Kunden, ohne berechnet zu sein (Belegart 'consignment_delivery', KL-: Umlagerung
// an den Standort „Kommission beim Kunden“). Nach dem Fest wird zurückgezählt: Volle Gebinde kommen mit einer
// Kommissionsrücknahme ('consignment_return', KR-) zurück in den Bestand, der Rest ist verbraucht und wird als
// gewöhnlicher Lieferschein berechenbar – geliefert − zurück = berechnet, der Kommissionsbestand endet bei null.
// Mengen „im Gebinde“ in Tausendstel des Gebindes der Zeile.
import { defineContract } from '@shared/contract'
import { defineDocumentContracts } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { CONSIGNMENT_DELIVERY_TYPE, CONSIGNMENT_RETURN_TYPE, ORDER_TYPE } from '../lib/labels'
import { zCustomerRef, zDay, zDocumentRef, zLocationRef, zNote, zUnitQty, zUnitQtyOrZero } from './common'

/** Funktionsschalter „Kommission“ (Branchenprofil): ohne ihn gibt es diese Aktionen für den Betrieb nicht */
const FEATURE = 'consignment'

const PERMISSIONS = { read: 'sales.consignment.read', write: 'sales.consignment.write', post: 'sales.consignment.write', reverse: 'sales.consignment.write' } as const

export const consignment = defineDocumentContracts({
  feature: FEATURE,
  module: 'sales',
  entity: 'consignment',
  documentType: CONSIGNMENT_DELIVERY_TYPE,
  title: 'Kommissionslieferung',
  titlePlural: 'Kommissionslieferungen',
  permissions: PERMISSIONS,
  derivesFrom: [ORDER_TYPE],
  keywords: ['Kommission', 'Kommissionsware', 'Festservice', 'Fest', 'auf Kommission', 'KL'],
})

export const consignmentGet = consignment.get
export const consignmentList = consignment.list
export const consignmentPrint = consignment.print
/** Storno, solange nichts zurückgezählt ist: Die Ware kommt vom Kommissionsstandort zurück */
export const consignmentReverse = consignment.reverse

export const consignmentBack = defineDocumentContracts({
  feature: FEATURE,
  module: 'sales',
  entity: 'consignmentReturn',
  documentType: CONSIGNMENT_RETURN_TYPE,
  title: 'Kommissionsrücknahme',
  titlePlural: 'Kommissionsrücknahmen',
  permissions: PERMISSIONS,
  derivesFrom: [CONSIGNMENT_DELIVERY_TYPE],
  keywords: ['Kommissionsrücknahme', 'Rückzählung', 'volle Kästen zurück', 'KR'],
})

export const consignmentReturnGet = consignmentBack.get
export const consignmentReturnList = consignmentBack.list
export const consignmentReturnPrint = consignmentBack.print

export const consignmentDeliver = defineContract({
  name: 'sales.consignment.deliver',
  title: 'Kommissionsware zum Kunden liefern',
  description: 'Lagert die Ware eines bestätigten Kommissionsauftrags an den Standort „Kommission beim Kunden“ um – berechnet wird noch nichts.',
  llm: {
    description:
      'Liefert einen bestätigten Auftrag der Art Kommission aus: Die offenen Mengen (oder die in `lines` genannten, im ' +
      'Gebinde der Auftragszeile, Tausendstel) gehen vom liefernden Standort an den Kommissionsstandort. Es entsteht ' +
      'eine Kommissionslieferung (KL-…); Bestand wird nicht verkauft, Pfand nicht berechnet. Nach dem Fest: ' +
      'sales.consignment.settle. Für gewöhnliche Lieferungen sales.delivery.pick.',
    keywords: ['Kommission liefern', 'Festware ausliefern', 'auf Kommission'],
  },
  input: z.strictObject({
    orderId: zId,
    lines: z.array(z.strictObject({ orderLineId: zId, deliverQty: zUnitQty })).max(500).optional(),
    documentDate: zDay.optional(),
    note: zNote,
  }),
  output: z.object({ consignmentId: zId, number: z.string().nullable(), message: z.string() }),
  permission: PERMISSIONS.write,
  feature: FEATURE,
  risk: 'financial',
  scope: 'record',
  limits: { maxItems: 500, itemCount: (input) => input.lines?.length ?? 1 },
})

export const consignmentSettle = defineContract({
  name: 'sales.consignment.settle',
  title: 'Kommission zurückzählen und abrechnen',
  description: 'Bucht die zurückgekommenen vollen Gebinde zurück in den Bestand; der Rest gilt als verbraucht und wird als Lieferschein abrechenbar.',
  llm: {
    description:
      'Rechnet eine Kommissionslieferung nach dem Fest ab. Je Zeile `returnedQty` = zurückgekommene volle Gebinde im ' +
      'Gebinde der Zeile (Tausendstel; nicht genannte Zeilen: nichts zurück). Was zurückkommt, wird per ' +
      'Kommissionsrücknahme wieder Bestand am liefernden Standort; geliefert − zurück ist der Verbrauch und wird als ' +
      'zugestellter Lieferschein gebucht (Verkauf ab Kommissionsstandort, Pfand auf das Leergutkonto) – nur er ist ' +
      'abrechenbar. `fees` hängt Dienstleistungsartikel an (Bearbeitungsgebühr, Anfahrt). Danach ist die Kommission ' +
      '„Abgerechnet“. Leeres Leergut zählt deposits.customerReturn.book.',
    keywords: ['Kommission abrechnen', 'Rückzählung', 'Endabrechnung', 'volle Kästen zurück', 'Verbrauch berechnen'],
  },
  input: z.strictObject({
    id: zId,
    lines: z.array(z.strictObject({ lineId: zId, returnedQty: zUnitQtyOrZero })).max(500).default([]),
    /** Dienstleistungsartikel auf dem Verbrauchs-Lieferschein: Artikel und Menge im Verkaufsgebinde (Tausendstel) */
    fees: z.array(z.strictObject({ itemId: zId, feeQty: zUnitQty, note: zNote })).max(20).default([]),
    documentDate: zDay.optional(),
    note: zNote,
  }),
  output: z.object({
    id: zId,
    returnId: zId.nullable(),
    returnNumber: z.string().nullable(),
    /** Lieferschein über den Verbrauch – null, wenn alles zurückkam und keine Gebühr anfällt */
    deliveryId: zId.nullable(),
    deliveryNumber: z.string().nullable(),
    billableGrossCents: z.int(),
    message: z.string(),
  }),
  permission: PERMISSIONS.write,
  feature: FEATURE,
  risk: 'financial',
  scope: 'record',
  limits: { maxItems: 520, itemCount: (input) => input.lines.length + input.fees.length + 1 },
})

export const consignmentDetail = defineContract({
  name: 'sales.consignment.detail',
  title: 'Stand einer Kommission anzeigen',
  description: 'Je Zeile gelieferte, zurückgekommene und verbrauchte Menge einer Kommissionslieferung mit den Folgebelegen.',
  llm: {
    description:
      'Liefert zu einer Kommissionslieferung je Zeile: geliefert, zurück, verbraucht (berechnet) und noch beim Kunden ' +
      '(im Gebinde der Zeile, Tausendstel), dazu Auftrag, Rücknahmen und den Lieferschein über den Verbrauch. ' +
      'Verwenden für „Was steht noch bei der Fachschaft?“.',
    keywords: ['Kommission', 'beim Kunden', 'Rückzählung', 'Verbrauch'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    customer: zCustomerRef.nullable(),
    location: zLocationRef,
    consignmentLocation: zLocationRef.nullable(),
    documentDate: z.string(),
    orderId: zId.nullable(),
    orderNumber: z.string().nullable(),
    lines: z.array(
      z.object({
        lineId: zId,
        pos: z.int(),
        itemId: zId.nullable(),
        sku: z.string().nullable(),
        description: z.string().nullable(),
        unit: z.string().nullable(),
        deliveredQty: z.int(),
        returnedQty: z.int(),
        consumedQty: z.int(),
        /** noch beim Kunden: geliefert − zurück − verbraucht */
        outQty: z.int(),
      }),
    ),
    documents: z.array(zDocumentRef),
    canSettle: z.boolean(),
  }),
  permission: PERMISSIONS.read,
  feature: FEATURE,
  risk: 'read',
  scope: 'record',
})

export const consignmentOpen = defineContract({
  name: 'sales.consignment.open',
  title: 'Kommissionen beim Kunden anzeigen',
  description: 'Kommissionslieferungen, die noch nicht zurückgezählt sind – mit Kunde, Tag und Menge.',
  llm: {
    description: 'Listet Kommissionslieferungen im Zustand „Beim Kunden“ (noch nicht abgerechnet) mit Kunde, Lieferdatum, geplantem Rückgabetag und Gebindezahl. Verwenden für „Welche Kommissionen sind noch draußen?“.',
    keywords: ['offene Kommissionen', 'Kommission draußen', 'Feste'],
  },
  input: z.strictObject({ partnerId: zId.optional(), all: z.boolean().default(false), limit: z.int().min(1).max(200).default(100) }),
  output: z.object({
    rows: z.array(
      z.object({
        id: zId,
        number: z.string().nullable(),
        status: z.string(),
        statusLabel: z.string(),
        customer: zCustomerRef.nullable(),
        locationCode: z.string(),
        documentDate: z.string(),
        /** geplanter Tag der Rückzählung (aus dem Auftrag) */
        eventEnd: z.string().nullable(),
        orderNumber: z.string().nullable(),
        summary: z.string(),
        lineCount: z.int(),
      }),
    ),
  }),
  permission: PERMISSIONS.read,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})
